import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { getRegionProfile } from './config/region';
import zh from './locales/zh.json';
import en from './locales/en.json';
import ja from './locales/ja.json';

/** 与后端一致的 locale：zh / en / ja */
export type BackendLocale = 'zh' | 'en' | 'ja';

const profile = getRegionProfile();

// cn 区域只打包 zh；global 区域全量支持
const allResources = {
  zh: { translation: zh },
  en: { translation: en },
  ja: { translation: ja },
};

// 根据区域决定实际挂载的资源（cn 只需要 zh，减少包体积）
const resources: typeof allResources =
  profile.i18nMode === 'single'
    ? { zh: allResources.zh, en: allResources.en, ja: allResources.ja }
    : allResources;

/** 从 LocaleCode (zh-CN, en-US, ja-JP) 映射到 i18next / 后端 locale */
export function storeCodeToLocale(code: string): BackendLocale {
  if (code.startsWith('zh')) return 'zh';
  if (code.startsWith('en')) return 'en';
  if (code.startsWith('ja')) return 'ja';
  // cn 区域默认回退到 zh，global 回退到 en
  return profile.region === 'cn' ? 'zh' : 'en';
}

/** 将 RegionProfile defaultLocale 转为 BackendLocale */
function profileDefaultLocale(): BackendLocale {
  return storeCodeToLocale(profile.defaultLocale);
}

/** 当前 i18n 语言（zh/en/ja），供 API 请求头 X-Language 使用 */
export function getCurrentLocale(): BackendLocale {
  const l = i18n.language?.toLowerCase();
  if (l === 'en' || l === 'ja') return l as BackendLocale;
  return 'zh';
}

const defaultLng = profileDefaultLocale();
const fallbackLng = defaultLng;

i18n.use(initReactI18next).init({
  resources,
  lng: defaultLng,
  fallbackLng,
  interpolation: { escapeValue: false },
});

// 与 languageStore 持久化同步：从 localStorage 恢复后设置 i18n 语言
// cn 区域忽略恢复（固定中文），global 区域仅在用户明确选择过语言时恢复
if (profile.i18nMode === 'multi') {
  // 使用与 languageStore 一致的 storage key（带区域信息）
  const STORAGE_KEY = `language-storage-${profile.region}`;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { state?: { language?: string; hasUserSelected?: boolean } };
      // 只有用户明确选择过语言时才恢复，否则使用默认语言（global 区域为英文）
      if (parsed?.state?.hasUserSelected && parsed?.state?.language) {
        const code = parsed.state.language;
        const locale = storeCodeToLocale(code);
        // 仅在区域支持的语言中恢复
        if (profile.supportedLocales.some((l) => l.startsWith(locale))) {
          i18n.changeLanguage(locale);
        }
      }
    }
  } catch {
    // ignore
  }
}

export default i18n;
